import { Button, Dropdown, Form, Menu, Modal, Select, Spin } from "antd";
import { saveAs } from "file-saver";
import range from "lodash/range";
import moment from "moment-timezone";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import api from "../../../api";
import { DISPLAY_DATE_FORMAT } from "../../../constants";
import { buildExportFileName, formatAmountCurrency } from "../../../utils";
import { ContentWrapper, InputWrapper } from "../../common/styledComponents";
import useEmailParam from "../../hooks/useEmailParam";
import { useLazyRequest } from "../../hooks/useRequest.hook";
import { Fieldset, Legend } from "../VatView/styledComponents";
import { ZM_ELSTER_PERIOD_YEAR } from "./constants";
import SubNavigation, { ZM_TABS } from "./SubNavigation";
import CustomerSearchInput from "@/lib/customerSearch/CustomerSearchInput";

const { Option } = Select;

const NO_ZM_TRANSACTIONS_WARNING = "No ZM transactions in the selected period";

enum ElsterFormFormat {
  XML = "application/vnd.elster+xml",
  JSON = "application/json",
}

interface ZmElsterRow {
  art: "L" | "S";
  vatNumber: string;
  amount: number;
}

interface ZmElsterPreview {
  accountId: number;
  firstName?: string;
  lastName?: string;
  legalName?: string;
  street?: string;
  postCode?: string;
  city?: string;
  vatNumber?: string;
  year: number;
  period: string;
  creationDate: string;
  transactionsCount: number;
  rows: ZmElsterRow[];
}

interface ZmElsterStatus {
  errors: string[];
  warnings: string[];
}

const formatValidationMessage = (message: string, t: TFunction<"ZmView">) => {
  if (
    message.startsWith("Missing recipient VAT ID transactions: ") ||
    message.startsWith("Recipient VAT ID must not be DE transactions: ")
  ) {
    const [prefix, ids] = message.split(": ");
    const translatedPrefix =
      prefix === "Missing recipient VAT ID transactions"
        ? t("elster.missingRecipientVatIds")
        : t("elster.recipientVatIdMustNotBeDe");

    return (
      <li key={message}>
        {translatedPrefix}
        <ul>
          {ids.split(",").map((id) => (
            <li key={id}>
              <a
                href={`/transaction-view?id=${id}`}
                target="_blank"
                rel="noreferrer"
              >
                {id}
              </a>
            </li>
          ))}
        </ul>
      </li>
    );
  }

  if (message === NO_ZM_TRANSACTIONS_WARNING) {
    return <li key={message}>{t("elster.noZmTransactions")}</li>;
  }

  return <li key={message}>{message}</li>;
};

const isCorrection = (warnings: string[] | undefined) =>
  !!warnings &&
  warnings.some((warning: string) => warning.startsWith("A ZM statement for"));

// ZM exports are named <Customer> - ZM - <Period> (SMT-1454).
// The name comes from the values the request was started with, not the
// current form, so changing customer or period mid-request cannot rename it.
const saveExport = async (
  blob: Blob,
  extension: "xml" | "pdf",
  request: { email: string; year: string; period: string }
) => {
  const user = await api.kontax.getExportCustomer(request.email);
  saveAs(
    blob,
    buildExportFileName({
      user,
      exportType: "ZM",
      period: {
        kind: "kontax",
        year: request.year,
        period: request.period,
      },
      extension,
    })
  );
};

const ElsterView = () => {
  const { t } = useTranslation("ZmView");
  const lastMonth = moment().subtract(1, "months");
  const preselectedYear = lastMonth.format("YYYY");
  const preselectedMonth = lastMonth.format("MM");
  const yearList = range(2020, moment().year() + 1);

  const [email, setEmail] = useEmailParam();
  const [period, setPeriod] = useState(preselectedMonth);
  const [year, setYear] = useState(preselectedYear);
  const [contentType, setContentType] = useState(ElsterFormFormat.XML);
  const [zmPreview, setZmPreview] = useState<ZmElsterPreview | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [zmStatus, setZmStatus] = useState<ZmElsterStatus | null>(null);
  const [isAlreadySubmitted, setIsAlreadySubmitted] = useState(false);
  const isPreviewDisabled =
    zmStatus?.warnings.includes(NO_ZM_TRANSACTIONS_WARNING) ?? false;
  const isEricButtonDisabled =
    !!zmStatus?.errors?.length || isPreviewDisabled || isAlreadySubmitted;

  const [getZmElster, getZmElsterResponse] = useLazyRequest(
    async (
      email: string,
      year: number,
      period: string,
      contentType: ElsterFormFormat
    ) => {
      setContentType(contentType);
      return api.kontax.getZmElster(email, year, period, contentType);
    }
  );

  const [getZmElsterStatus, getZmElsterStatusResponse] = useLazyRequest(
    async (email: string, year: number, period: string) => {
      return api.kontax.getZmElsterStatus(email, year, period);
    }
  );

  const [sendZmViaEric, sendZmViaEricResponse] = useLazyRequest(
    async (email: string, year: number, period: string) => {
      return api.kontax.sendZmViaEric(email, year, period);
    }
  );

  useEffect(() => {
    if (getZmElsterResponse.data) {
      switch (contentType) {
        case ElsterFormFormat.JSON:
          setZmPreview(JSON.parse(JSON.stringify(getZmElsterResponse.data)));
          setShowModal(true);
          getZmElsterResponse.data = undefined;
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType, getZmElsterResponse.data]);

  useEffect(() => {
    if (getZmElsterStatusResponse.data) {
      setZmStatus(JSON.parse(JSON.stringify(getZmElsterStatusResponse.data)));
      getZmElsterStatusResponse.data = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getZmElsterStatusResponse.data]);

  const resetForm = () => {
    setZmStatus(null);
    setZmPreview(null);
    setIsAlreadySubmitted(false);
  };

  const handleShow = useCallback(() => {
    getZmElsterStatus(email || "", Number.parseInt(year), period);
  }, [email, year, period, getZmElsterStatus]);

  const handlePreview = useCallback(() => {
    getZmElster(
      email || "",
      Number.parseInt(year),
      period,
      ElsterFormFormat.JSON
    );
  }, [email, year, period, getZmElster]);

  const handleDownloadXML = useCallback(async () => {
    const request = { email: email || "", year, period };
    const data = await getZmElster(
      request.email,
      Number.parseInt(year),
      period,
      ElsterFormFormat.XML
    );
    if (data) {
      await saveExport(
        new Blob([data], { type: "application/xml" }),
        "xml",
        request
      );
    }
  }, [email, year, period, getZmElster]);

  const handleSendViaERIC = useCallback(async () => {
    setIsAlreadySubmitted(true);
    const request = { email: email || "", year, period };
    const data = await sendZmViaEric(
      request.email,
      Number.parseInt(year),
      period
    );
    if (data) {
      await saveExport(
        new Blob([data], { type: "application/pdf" }),
        "pdf",
        request
      );
    }
  }, [email, year, period, sendZmViaEric]);

  const ActionMenu = useMemo(
    () => (
      <Menu>
        <Menu.Item key="DOWNLOAD_XML" onClick={handleDownloadXML}>
          {t("elster.downloadXml")}
        </Menu.Item>
        <Menu.Item
          key="SEND_VIA_ERIC"
          disabled={isEricButtonDisabled || sendZmViaEricResponse.isLoading}
          onClick={handleSendViaERIC}
        >
          {sendZmViaEricResponse.isLoading ? (
            <>
              <Spin size="small" /> {t("elster.sendingViaEric")}
            </>
          ) : (
            t("elster.sendViaEric")
          )}
          {isCorrection(zmStatus?.warnings) ? t("elster.correctionNotice") : ""}
        </Menu.Item>
      </Menu>
    ),
    [
      handleDownloadXML,
      handleSendViaERIC,
      isEricButtonDisabled,
      sendZmViaEricResponse.isLoading,
      t,
      zmStatus,
    ]
  );

  return (
    <ContentWrapper>
      <SubNavigation activeTab={ZM_TABS.ELSTER.key} />
      <Form>
        <Fieldset>
          <Legend>{t("elster.chooseClient")}</Legend>
          {/* Selects on Enter / Filter, resolved to the customer's real
              email, instead of on every keystroke (SMT-1396). */}
          <Form.Item label={t("elster.email")}>
            <CustomerSearchInput
              value={email}
              onSelect={(selected) => {
                setEmail(selected);
                resetForm();
              }}
            />
          </Form.Item>
          <InputWrapper>
            <Select
              id="zm-elster-period"
              style={{ width: "100%" }}
              value={period}
              onChange={(value) => {
                setPeriod(value);
                resetForm();
              }}
            >
              <Option value="01">{t("elster.months.01")}</Option>
              <Option value="02">{t("elster.months.02")}</Option>
              <Option value="03">{t("elster.months.03")}</Option>
              <Option value="04">{t("elster.months.04")}</Option>
              <Option value="05">{t("elster.months.05")}</Option>
              <Option value="06">{t("elster.months.06")}</Option>
              <Option value="07">{t("elster.months.07")}</Option>
              <Option value="08">{t("elster.months.08")}</Option>
              <Option value="09">{t("elster.months.09")}</Option>
              <Option value="10">{t("elster.months.10")}</Option>
              <Option value="11">{t("elster.months.11")}</Option>
              <Option value="12">{t("elster.months.12")}</Option>
              <Option value="Q1">{t("elster.quarters.Q1")}</Option>
              <Option value="Q2">{t("elster.quarters.Q2")}</Option>
              <Option value="Q3">{t("elster.quarters.Q3")}</Option>
              <Option value="Q4">{t("elster.quarters.Q4")}</Option>
              <Option value={ZM_ELSTER_PERIOD_YEAR}>{t("elster.year")}</Option>
            </Select>
          </InputWrapper>
          <InputWrapper>
            <Select
              style={{ width: "100%" }}
              id="zm-elster-year"
              value={year}
              onChange={(value) => {
                setYear(value);
                resetForm();
              }}
            >
              {yearList.map((value) => (
                <Option key={value} value={String(value)}>
                  {value}
                </Option>
              ))}
            </Select>
          </InputWrapper>
          <Button
            disabled={!email}
            size="large"
            type="primary"
            htmlType="button"
            onClick={handleShow}
          >
            {t("elster.show")}
          </Button>
        </Fieldset>
        {zmStatus && (
          <Fieldset style={{ maxWidth: "500px" }}>
            {zmStatus.errors.length ? (
              <section>
                <legend>{t("elster.errors")}</legend>
                <ul>
                  {zmStatus.errors.map((message) =>
                    formatValidationMessage(message, t)
                  )}
                </ul>
              </section>
            ) : null}
            {zmStatus.warnings.length ? (
              <section>
                <legend>{t("elster.warnings")}</legend>
                <ul>
                  {zmStatus.warnings.map((message) =>
                    formatValidationMessage(message, t)
                  )}
                </ul>
              </section>
            ) : null}
            <section>
              <Dropdown.Button
                size="large"
                overlay={ActionMenu}
                disabled={isPreviewDisabled || sendZmViaEricResponse.isLoading}
                loading={sendZmViaEricResponse.isLoading}
                onClick={handlePreview}
              >
                {sendZmViaEricResponse.isLoading
                  ? t("elster.sendingViaEricEllipsis")
                  : t("elster.preview")}
              </Dropdown.Button>
            </section>
          </Fieldset>
        )}
        {getZmElsterStatusResponse.error && (
          <p>
            {t("elster.requestError", {
              message: getZmElsterStatusResponse.error.message,
            })}
          </p>
        )}
        {getZmElsterResponse.error && (
          <p>
            {t("elster.requestError", {
              message: getZmElsterResponse.error.message,
            })}
          </p>
        )}
        {sendZmViaEricResponse.error && (
          <p>
            {t("elster.requestError", {
              message: sendZmViaEricResponse.error.message,
            })}
          </p>
        )}
      </Form>
      <Modal
        visible={showModal}
        title={t("elster.previewTitle", { accountId: zmPreview?.accountId })}
        footer={null}
        width={800}
        onCancel={() => setShowModal(false)}
      >
        <div style={{ float: "left" }}>
          <strong>{t("elster.date")}</strong>{" "}
          {moment(zmPreview?.creationDate).format(DISPLAY_DATE_FORMAT)}
          <br />
          <strong>{t("elster.period")}</strong> {zmPreview?.period}/
          {zmPreview?.year}
          <br />
          <br />
          <strong>{t("elster.name")}</strong> {zmPreview?.legalName}
          <br />
          <strong>{t("elster.street")}</strong> {zmPreview?.street}
          <br />
          <strong>{t("elster.city")}</strong> {zmPreview?.postCode}{" "}
          {zmPreview?.city}
        </div>
        <div style={{ float: "right" }}>
          <strong>{t("elster.rowsCount")}</strong> {zmPreview?.rows.length}
          <br />
          <strong>{t("elster.transactionsCount")}</strong>{" "}
          {zmPreview?.transactionsCount}
          <br />
          <br />
          <strong>{t("elster.ownVatId")}</strong> {zmPreview?.vatNumber}
        </div>
        <div style={{ clear: "both" }}></div>
        <table className="ustva">
          <thead>
            <tr>
              <th>{t("elster.recipientVatId")}</th>
              <th>{t("elster.type")}</th>
              <th>{t("elster.amount")}</th>
            </tr>
          </thead>
          <tbody>
            {zmPreview?.rows.map((row) => (
              <tr key={`${row.vatNumber}-${row.art}`}>
                <td>{row.vatNumber}</td>
                <td>{row.art}</td>
                <td>{formatAmountCurrency(row.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
    </ContentWrapper>
  );
};

export default ElsterView;
